import React, { useState } from 'react';
import type { Property } from 'csstype';
import { Image, useStyles, useTheme } from '@archipro-design/aria';
import type { LoadingValue } from '@archipro-design/aria';
import { PlayFill } from '@archipro-design/icons';
import * as S from './ImageSlide.style';

interface ImageSlideProps {
    libraryImage: string;
    onClick?: () => void;
    width: number;
    height: number;
    containerWidth?: number;
    containerHeight?: number;
    sizes?: string;
    loadingValue?: LoadingValue;
    objectFit?: Property.ObjectFit;
    objectPosition?: Property.ObjectPosition;
    isMultiplied?: boolean;
    alt?: string;
    quality?: number;
    isVideo?: boolean;
    caption?: string;
    /** Fill the parent's width (keeping the fixed px height) instead of a fixed-width box. */
    fluid?: boolean;
}

const ImageSlide = React.forwardRef<HTMLDivElement, ImageSlideProps>(
    (
        {
            libraryImage,
            isMultiplied,
            onClick,
            width,
            height,
            containerWidth,
            containerHeight,
            sizes,
            loadingValue = 'lazy',
            quality = 90,
            isVideo = false,
            caption,
            fluid = false,
            ...rest
        },
        ref,
    ) => {
        const [isVideoError, setIsVideoError] = useState(false);
        const { css } = useStyles({
            width: containerWidth ?? width,
            height: containerHeight ?? height,
            isVideo,
            isVideoError,
            fluid,
        });
        const theme = useTheme();

        return (
            <div ref={ref} className={css(S.PrimaryImageDesktopContainer)}>
                {/* Letterbox fill for non-multiplied images; scaled so blur soft-edge stays inside the clip. */}
                {!isMultiplied && (
                    <img
                        src={libraryImage}
                        alt=""
                        aria-hidden
                        draggable={false}
                        className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover select-none"
                        style={{ filter: 'blur(50px)' }}
                    />
                )}
                <Image
                    alt={rest.alt || ''}
                    src={libraryImage}
                    mixBlendMode={isMultiplied ? 'multiply' : 'normal'}
                    sizes={sizes}
                    width={width}
                    height={height}
                    layout={'responsive'}
                    objectFit="contain"
                    loading={loadingValue}
                    onClick={onClick}
                    onError={() => {
                        setIsVideoError(true);
                    }}
                    quality={quality}
                    {...rest}
                    variables={{
                        placeholderBackgroundColorDark: 'transparent',
                        placeholderBackgroundColorLight: isMultiplied
                            ? theme.siteVariables.colors.white['200A']
                            : 'transparent',
                    }}
                    overlay={
                        isVideo
                            ? {
                                  alwaysVisible: true,
                                  icon: <PlayFill />,
                                  variables: {
                                      backgroundColor: 'rgb(0 0 0 / 0.2)',
                                  },
                              }
                            : undefined
                    }
                />
                {caption && (
                    <p className={`pt-4 ${css(S.ImageCaption)}`}>{caption}</p>
                )}
            </div>
        );
    },
);

ImageSlide.displayName = 'ImageSlide';

export default ImageSlide;
